import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_URL from "../../utils/apiUrl";

// Fallback DPA text in case backend is unavailable. The backend can override
// this via /api/onboarding/dpa-text, but signup must work even if backend is
// down/old (e.g. during platform updates).
const FALLBACK_DPA = {
  version: "1.0",
  body: "Data Processing Agreement v1.0 — Between the Platform Operator (Master) and the Tenant. The Tenant acts as Data Controller for end-user data on their leased Command Center. The Master acts as Data Processor and stores data in the region specified at tenant creation. The Tenant may request a full data export or irreversible deletion at any time.",
};

export default function PublicSignup() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    company_name: "", owner_email: "", password: "", data_region: "EU",
    accepted_dpa: false, signer_name: "",
  });
  const [dpa, setDpa] = useState(FALLBACK_DPA);
  const [emailOk, setEmailOk] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    // Try to fetch latest DPA from backend; ignore failures (we already have fallback)
    fetch(`${API_URL}/api/onboarding/dpa-text`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.body) setDpa(d); })
      .catch(() => {});
  }, []);

  async function checkEmail() {
    setErr("");
    if (!form.owner_email) return;
    try {
      const r = await fetch(`${API_URL}/api/onboarding/check-email`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.owner_email }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      setEmailOk(d.available);
    } catch (e) { setErr(e.message); }
  }

  async function submit() {
    setErr("");
    try {
      const r = await fetch(`${API_URL}/api/onboarding/signup`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Signup failed");
      setDone(d);
    } catch (e) { setErr(e.message); }
  }

  return (
    <div data-testid="signup-page" style={page}>
      <div style={panel}>
        {!done ? (
          <>
            <div style={{ fontSize: 11, letterSpacing: 3, opacity: 0.5, marginBottom: 6 }}>PLATFORM</div>
            <h1 style={{ fontSize: 26, margin: "0 0 4px", letterSpacing: -0.5 }}>Start your tenancy</h1>
            <div style={{ fontSize: 13, opacity: 0.55, marginBottom: 24 }}>
              Step {step} of 3
            </div>

            {err && <div style={errBox}>{err}</div>}

            {step === 1 && (
              <>
                <Field label="Company name *">
                  <input data-testid="signup-company" required value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} style={inp} />
                </Field>
                <Field label="Owner email *">
                  <input data-testid="signup-email" type="email" required value={form.owner_email} onChange={e => { setForm({...form, owner_email: e.target.value}); setEmailOk(null); }} onBlur={checkEmail} style={inp} />
                  {emailOk === false && <div style={{ color: "#ff7886", fontSize: 12, marginTop: 4 }}>Email already in use.</div>}
                  {emailOk === true && <div style={{ color: "#34d399", fontSize: 12, marginTop: 4 }}>Available ✓</div>}
                </Field>
                <Field label="Password * (min 8 chars)">
                  <input data-testid="signup-password" type="password" required minLength={8} value={form.password} onChange={e => setForm({...form, password: e.target.value})} style={inp} />
                </Field>
                <Field label="Data region">
                  <select value={form.data_region} onChange={e => setForm({...form, data_region: e.target.value})} style={inp}>
                    <option value="EU">EU (GDPR)</option>
                    <option value="US">US (CCPA)</option>
                  </select>
                </Field>
                <button data-testid="signup-next-1" disabled={!form.company_name || !form.owner_email || !form.password || emailOk === false} onClick={() => setStep(2)} style={btnPrimary}>
                  Continue
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <Field label={`Data Processing Agreement v${dpa?.version || "?"}`}>
                  <div style={{ background: "#0a0816", border: "1px solid #2a1c44", padding: 14, borderRadius: 8, fontSize: 12, opacity: 0.8, maxHeight: 200, overflow: "auto", lineHeight: 1.6 }}>
                    {dpa?.body || "Loading…"}
                  </div>
                </Field>
                <Field label="Signer full name *">
                  <input data-testid="signup-signer" value={form.signer_name} onChange={e => setForm({...form, signer_name: e.target.value})} style={inp} />
                </Field>
                <label style={{ display: "flex", gap: 8, alignItems: "center", margin: "10px 0 16px", fontSize: 13, opacity: 0.85, cursor: "pointer" }}>
                  <input data-testid="signup-accept" type="checkbox" checked={form.accepted_dpa} onChange={e => setForm({...form, accepted_dpa: e.target.checked})} />
                  I accept the DPA on behalf of <strong>{form.company_name}</strong>.
                </label>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => setStep(1)} style={btnGhost}>Back</button>
                  <button data-testid="signup-next-2" disabled={!form.accepted_dpa || !form.signer_name} onClick={() => setStep(3)} style={btnPrimary}>Continue</button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div style={{ background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.3)", padding: 16, borderRadius: 10, fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Review</div>
                  <div>Company: <strong>{form.company_name}</strong></div>
                  <div>Email: <strong>{form.owner_email}</strong></div>
                  <div>Region: <strong>{form.data_region}</strong></div>
                  <div>DPA signed by: <strong>{form.signer_name}</strong></div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => setStep(2)} style={btnGhost}>Back</button>
                  <button data-testid="signup-submit" onClick={submit} style={btnPrimary}>Create tenant</button>
                </div>
              </>
            )}
          </>
        ) : (
          <div data-testid="signup-success">
            <div style={{ fontSize: 11, letterSpacing: 3, opacity: 0.5, marginBottom: 6 }}>WELCOME</div>
            <h1 style={{ fontSize: 26, margin: "0 0 16px" }}>Tenant created ✓</h1>
            <div style={{ fontSize: 13, lineHeight: 1.7, opacity: 0.85 }}>
              Tenant id: <code style={{ background: "#0a0816", padding: "2px 6px", borderRadius: 4 }}>{done.tenant_id}</code><br/>
              Admin email: <strong>{done.admin_email}</strong><br/><br/>
              {done.next_step}
            </div>
            <button onClick={() => nav("/")} style={{ ...btnPrimary, marginTop: 18 }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 11, letterSpacing: 1.5, opacity: 0.7, marginBottom: 6, textTransform: "uppercase" }}>{label}</label>
      {children}
    </div>
  );
}

const page = { minHeight: "100vh", background: "radial-gradient(ellipse at top, #1a0b2e 0%, #050309 60%)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "#e9e6f2" };
const panel = { width: "min(540px, 100%)", background: "rgba(20,12,35,0.65)", border: "1px solid #3a2a55", padding: "40px 36px", borderRadius: 18, boxShadow: "0 30px 80px -10px rgba(0,0,0,0.7)", backdropFilter: "blur(18px)" };
const inp = { width: "100%", padding: "11px 13px", background: "rgba(255,255,255,0.03)", border: "1px solid #3a2a55", borderRadius: 10, color: "#fff", fontSize: 14, fontFamily: "inherit", outline: "none" };
const btnPrimary = { padding: "11px 18px", background: "linear-gradient(135deg,#8b5cf6,#a855f7)", color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 14, fontFamily: "inherit", flex: 1 };
const btnGhost = { padding: "11px 18px", background: "transparent", color: "#a89ec9", border: "1px solid #3a2a55", borderRadius: 10, cursor: "pointer", fontSize: 14, fontFamily: "inherit" };
const errBox = { padding: 12, background: "rgba(255,80,90,0.1)", border: "1px solid rgba(255,80,90,0.4)", color: "#ff7886", borderRadius: 8, fontSize: 13, marginBottom: 14 };
